import type { RecapEvent } from './world-recap';

export interface WorldMeetingTurn {
  id: string; at: number; agentId?: string; agentName?: string; text: string; summary?: boolean;
}
export interface WorldMeeting {
  id: string; title: string; startedAt: number; finished: boolean; failed: boolean; turns: WorldMeetingTurn[];
}
export interface WorldMeetingView {
  id: string; title: string; startedAt: number; agentIds: string[]; speakerId?: string; turns: WorldMeetingTurn[];
}
const MAX_MEETING_MS = 6 * 60 * 60_000;

function turns(meeting: WorldMeeting) {
  return [...new Map(meeting.turns.filter(turn => Number.isFinite(turn.at) && turn.at >= meeting.startedAt && turn.text.trim())
    .map(turn => [turn.id, turn])).values()].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}
function excerpt(text: string) {
  const plain = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/(^|\n)\s*(?:#{1,6}|>|[-*])\s+/g, '$1')
    .replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim();
  return plain.length > 280 ? `${plain.slice(0, 277).trimEnd()}…` : plain;
}

export function worldMeetingAt(meeting: WorldMeeting | null | undefined, at: number, recorded = false): WorldMeetingView | undefined {
  if (!meeting || !meeting.id || meeting.failed || !Number.isFinite(meeting.startedAt) || at < meeting.startedAt) return;
  const ordered = turns(meeting);
  const end = meeting.finished ? ordered[ordered.length - 1]?.at : meeting.startedAt + MAX_MEETING_MS;
  if (recorded ? end === undefined || at > end : meeting.finished || at - meeting.startedAt >= MAX_MEETING_MS) return;
  const available = ordered.filter(turn => turn.at <= at);
  // Attendance is meeting metadata; the actual words are revealed only at their recorded time.
  const attendees = recorded ? ordered : available;
  return { id: meeting.id, title: meeting.title, startedAt: meeting.startedAt,
    agentIds: [...new Set(attendees.flatMap(turn => turn.agentId ? [turn.agentId] : []))],
    speakerId: available[available.length - 1]?.agentId || (available.length ? 'maestro' : undefined),
    turns: available.slice(-3).map(turn => ({ ...turn, text: excerpt(turn.text) })) };
}

export function meetingRecap(events: RecapEvent[], meeting: WorldMeeting | null | undefined, until: number): RecapEvent[] {
  if (!meeting || meeting.failed || !Number.isFinite(meeting.startedAt) || !events.length) return events;
  const since = events[0].at;
  const allTurns = turns(meeting);
  const ordered = allTurns.filter(turn => turn.at <= until);
  const additions: RecapEvent[] = [{ id: `meeting:${meeting.id}:start`, at: meeting.startedAt, title: meeting.title,
    text: '', source: 'meeting', state: 'working', milestone: 'meeting', meetingId: meeting.id },
    ...ordered.map(turn => ({ id: `meeting:${meeting.id}:${turn.id}`, at: turn.at, title: meeting.title,
      text: excerpt(turn.text), source: 'meeting' as const, state: 'working' as const, meetingId: meeting.id }))];
  const end = allTurns[allTurns.length - 1]?.at;
  if (meeting.finished && end !== undefined) additions.push({ id: `meeting:${meeting.id}:end`, at: end + 1,
    title: meeting.title, text: '', source: 'meeting', state: 'done', meetingId: meeting.id });
  const middle = [...events.slice(1, -1).filter(event => event.source !== 'meeting'), ...additions.filter(event => event.at >= since && event.at <= until)]
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
  return [events[0], ...middle, events[events.length - 1]];
}

export function recapEventDuration(event: RecapEvent | undefined): number {
  return event?.source === 'meeting' && event.state !== 'done' ? 6 : 1.5;
}
